/**
 * Birthplace lookup — resolves a free-text city name to coordinates + IANA
 * timezone, needed by the real ephemeris calculation (lib/ephemeris/chart.ts)
 * for both the Ascendant/Lagna (needs lat/lon) and the historically-correct
 * UTC offset (needs the IANA zone, see lib/ephemeris/timezone.ts).
 *
 * Data source: GeoNames (https://www.geonames.org, CC-BY 4.0 — see
 * public/data/cities.json's provenance below), the same open dataset the
 * commercial reference calculator's Atlas.aspx step is built on per
 * docs/reference-calculator.md section 2 ("starker Hinweis, dass die
 * zugrunde liegende Ortsdatenbank auf GeoNames basiert"). We ship a static,
 * pre-filtered extract instead of calling a live geocoding API so this
 * works with zero network dependency at request time and with no
 * rate-limit/ToS exposure (GeoNames' own web service has strict per-hour
 * quotas unsuitable for a public birth-chart form). Extract: all cities
 * with population >= 5,000 in DE/AT/CH/FR/ES (our primary i18n markets, see
 * src/i18n/content.ts) plus population >= 50,000 worldwide, from GeoNames'
 * `cities15000.txt` dump (2026-07-27), reduced to
 * [name, asciiname, countryCode, lat, lon, population, ianaTimezone] tuples
 * — ~14.4k rows, ~300KB gzipped. Attribution: "Geo data © GeoNames.org,
 * CC-BY 4.0" (see Footer.tsx / Datenschutz.tsx for where this should be
 * surfaced in the UI — tracked as a follow-up, not blocking the ephemeris
 * swap itself).
 */

export type CityMatch = {
  name: string;
  asciiname: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  population: number;
  timezone: string;
};

type CityRow = [string, string, string, number, number, number, string];

let citiesPromise: Promise<CityRow[]> | null = null;

function loadCities(): Promise<CityRow[]> {
  if (!citiesPromise) {
    citiesPromise = fetch("/data/cities.json")
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load city database (${res.status})`);
        return res.json();
      })
      .then((rows: CityRow[]) => rows);
  }
  return citiesPromise;
}

function rowToMatch(row: CityRow): CityMatch {
  const [name, asciiname, countryCode, latitude, longitude, population, timezone] = row;
  return { name, asciiname: asciiname || name, countryCode, latitude, longitude, population, timezone };
}

/**
 * Finds the best-matching city for free-text input, optionally constrained
 * to a specific ISO-3166-1 alpha-2 country code.
 *
 * WHY A COUNTRY PARAMETER: the birth-chart form previously had no country
 * field at all, so a city name alone had to disambiguate itself — fine for
 * "Berlin" (effectively unique), silently wrong for any city name that
 * exists in multiple countries (there are dozens of towns named
 * "Springfield", "San Jose", "Alexandria", etc. across different
 * countries/timezones). Passing `countryCode` (now collected explicitly in
 * the form, see BirthChartForm.tsx/Hero.tsx's new country select) filters
 * candidates to that country FIRST, before falling back to the
 * population-based disambiguation below — this is what actually fixes
 * wrong-timezone/wrong-DST results for ambiguous city names, not just a
 * cosmetic form field.
 *
 * Matching within the (optionally country-filtered) candidate set is
 * case-insensitive and tries, in order: exact name match, exact ASCII-name
 * match (handles "Munchen" for "München" the same way the reference
 * calculator's help text asks users to type it — see
 * docs/reference-calculator.md section 2), then a substring/startsWith
 * fallback. Among matches, the highest-population city wins (rows are
 * pre-sorted by population descending in the data file, see cityLookup's
 * module header / public/data/cities.json generation script) — same
 * disambiguation heuristic as picking the "Berlin, Berlin" capital instead
 * of a same-named small hamlet when no country is given at all.
 *
 * Returns null if no city matches at all (caller should show a
 * "Geburtsort nicht gefunden" error rather than silently guessing).
 */
export async function findCity(query: string, countryCode?: string): Promise<CityMatch | null> {
  const rows = await loadCities();
  const q = query.trim().toLowerCase();
  if (!q) return null;

  const pool = countryCode ? rows.filter((r) => r[2] === countryCode) : rows;

  const searchIn = (candidates: CityRow[]): CityMatch | null => {
    const exact = candidates.find((r) => r[0].toLowerCase() === q || (r[1] && r[1].toLowerCase() === q));
    if (exact) return rowToMatch(exact);
    const prefix = candidates.find(
      (r) => r[0].toLowerCase().startsWith(q) || (r[1] && r[1].toLowerCase().startsWith(q))
    );
    if (prefix) return rowToMatch(prefix);
    const contains = candidates.find(
      (r) => r[0].toLowerCase().includes(q) || (r[1] && r[1].toLowerCase().includes(q))
    );
    return contains ? rowToMatch(contains) : null;
  };

  const withinCountry = searchIn(pool);
  if (withinCountry) return withinCountry;

  // If a country was given but nothing matched inside it (e.g. a typo in
  // the city name, or a small place below our population cutoff — see
  // cityLookup's module header for the dataset's population thresholds),
  // fall back to the unrestricted search rather than reporting "not found"
  // outright — better to find SOME plausible match than none, same as the
  // reference calculator's own "pick a larger nearby place" guidance (see
  // docs/reference-calculator.md section 2).
  if (countryCode) return searchIn(rows);
  return null;
}

/** Returns up to `limit` candidate cities for autocomplete-style UIs (unused by the current form, which is single-shot, but exposed for the announced follow-up of a proper city-autocomplete input). */
export async function suggestCities(query: string, limit = 8, countryCode?: string): Promise<CityMatch[]> {
  const rows = await loadCities();
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const pool = countryCode ? rows.filter((r) => r[2] === countryCode) : rows;
  return pool
    .filter((r) => r[0].toLowerCase().startsWith(q) || (r[1] && r[1].toLowerCase().startsWith(q)))
    .slice(0, limit)
    .map(rowToMatch);
}

/**
 * All distinct ISO-3166-1 alpha-2 country codes present in the bundled
 * dataset, each with its German display name (via `Intl.DisplayNames` —
 * built into every evergreen browser, so this needs no hand-maintained
 * country-name list), sorted alphabetically by that display name for a
 * sensible dropdown order. Used by the new country `<select>` in the birth
 * form (BirthChartForm.tsx / Hero.tsx).
 */
export async function listCountries(): Promise<{ code: string; name: string }[]> {
  const rows = await loadCities();
  const codes = new Set(rows.map((r) => r[2]));
  const displayNames = new Intl.DisplayNames(["de"], { type: "region" });
  return [...codes]
    .map((code) => ({ code, name: displayNames.of(code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, "de"));
}
